import { action, autorun, comparer, computed, makeObservable, observable, reaction } from 'mobx'
import { getError, getValue, isPending } from 'mobx-async'

import {
  CaseBasicDetailsUpdate,
  CaseInputs,
  CaseRecordType,
  CaseSorting,
  SortOrder,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { DEFAULT_EXPORT_RECORDS_LIMIT, formatDate } from '@avantstay/backoffice-core'
import { ColumnConfig } from '@avantstay/data-grid'

import { RoutingStore } from '@new/__global/stores/RoutingStore'
import { SidePanelStore, SidePanelType } from '@new/__global/stores/SidePanelStore'
import { exportDataToCSV } from '@new/__global/utils/exportDataToCsv'
import { handleStoreError } from '@new/__global/utils/handleStoreError'
import { formatPeriodDates } from '@new/domains/operations/screens/CasesV2/CasesV2.constants'
import {
  IsgiTypes,
  LogicalTypes,
  RecurringTypes,
  SearchCaseRowProps,
  SearchCaseValue,
} from '@new/domains/operations/screens/CasesV2/CasesV2.types'
import * as arriere from '@new/domains/operations/screens/CasesV2/service/cases'
import { getBookingRatingExportValues } from '@new/domains/operations/screens/CasesV2/utils/bookingRating'
import { getCaseAge } from '@new/domains/operations/screens/CasesV2/utils/getCaseAge'

import { mountRouteUrl, ROUTES } from '@routes'
import { computedRouteParam } from '@stores/computedRouteParam'

import { getEnrichmentColumnKeys } from '../service/caseEnrichment'
import { casesFiltersStore } from './CasesFiltersStore'

const parseLogicalFilter = (appliedFilter: string[] | undefined, filterType: string) => {
  return appliedFilter?.length === 1 ? appliedFilter[0] === filterType : undefined
}

interface SelectedRowProps {
  rowIndex: number
  rowData: SearchCaseRowProps
}

export class _CasesStore {
  constructor() {
    makeObservable<
      _CasesStore,
      'caseFilters' | 'enrichmentColumnKeys' | 'casesPromise' | 'getCaseInputsPromise'
    >(this, {
      gridColumns: observable,
      refresh: observable,
      editionInline: observable,
      selectedRowIndexes: observable,
      caseDetailsId: computed,
      caseFilters: computed({ equals: comparer.structural }),
      enrichmentColumnKeys: computed({ equals: comparer.structural }),
      casesPromise: computed,
      getCaseInputsPromise: computed,
      searchCaseOwners: action.bound,
      searchCaseVendors: action.bound,
      caseInputs: computed({ keepAlive: true }),
      cases: computed,
      hasNextPage: computed,
      isLoadingCases: computed,
      casesError: computed,
      isLoadingCaseInputs: computed,
      isAllRowsSelected: computed,
      isBullEditingCaseWithExpenseAndVendor: computed,
      reloadTable: action.bound,
      setGridColumns: action.bound,
      setEditInLine: action.bound,
      updateCaseBasic: action.bound,
      setCaseDetailsId: action.bound,
      exportCSV: action.bound,
      setSelectedRowIndexes: action.bound,
      overrideRowSelection: action.bound,
      selectAllRowIndexes: action.bound,
    })

    autorun(() => {
      if (this.caseDetailsId) {
        SidePanelStore.setCurrentSidePanel(SidePanelType.caseDetails)
      } else if (!this.selectedArrivalChecklistHomeId.get()) {
        SidePanelStore.setCurrentSidePanel(undefined)
      }
    })

    reaction(
      () => RoutingStore.location.pathname,
      pathname => {
        if (pathname !== mountRouteUrl(ROUTES.operationCases.path)) {
          this.editionInline = false
        }
      },
    )
    reaction(
      () => this.caseDetailsId,
      id => {
        if (id) {
          SidePanelStore.setCurrentSidePanel(
            this.editionInline ? SidePanelType.editCase : SidePanelType.caseDetails,
          )
        }
      },
    )
  }

  gridColumns: ColumnConfig<SearchCaseRowProps>[] | undefined = undefined
  refresh = 0
  editionInline = false
  selectedRowIndexes: Map<string, SelectedRowProps> = new Map()

  private caseDetailsIdParam = computedRouteParam<string | undefined>('case', {
    patterns: [
      ROUTES.operationCases.path,
      ROUTES.operationCasesTapeChart.path,
      ROUTES.bookingCases.path,
      ROUTES.propertyDistributionChannels.path,
      ROUTES.operationGuestDamagesReport.path,
      ROUTES.sfdcWorkOrder.path,
      ROUTES.operationWorkOrder.path,
      ROUTES.operationExpenses.path,
      ROUTES.fastTapeChart.path,
      ROUTES.hotelTapeChart.path,
      ROUTES.propertyOwnerStatements.path,
      ROUTES.operationVendorEmailInvoice.path,
    ],
    defaultValue: undefined,
  })

  get caseDetailsId() {
    return this.caseDetailsIdParam.get()
  }

  private selectedArrivalChecklistHomeId = computedRouteParam<string | undefined>(
    'selectedArrivalChecklistHomeId',
    {
      patterns: [ROUTES.fastTapeChart.path, ROUTES.hotelTapeChart.path],
      defaultValue: undefined,
    },
  )

  private get caseFilters() {
    const {
      page,
      pageSize,
      dueDate,
      inStayGuestIssue,
      isRecurringCaseStatus,
      hasExpense,
      isAirbnbTripIssuesOnly,
      createdAtPeriod,
      quickFilter,
      types,
      aiGenerated,
      recordTypes,
      ...otherFilters
    } = casesFiltersStore.filteringArgs
    const quickFilterType = casesFiltersStore.quickFilterType
    const quickFilterDate = casesFiltersStore.quickFilterDate

    const inStayGuestIssueFilter = parseLogicalFilter(inStayGuestIssue, IsgiTypes.isgi)
    const recurringFilter = parseLogicalFilter(isRecurringCaseStatus, RecurringTypes.recurring)
    const hasExpenseFilter = parseLogicalFilter(hasExpense, LogicalTypes.yes)
    const aiGeneratedFilter = parseLogicalFilter(aiGenerated, LogicalTypes.yes)
    const airbnbTripIssueFilter = parseLogicalFilter(isAirbnbTripIssuesOnly, LogicalTypes.yes)

    const quickFilterDateField = quickFilterDate ? quickFilterDate.split('_') : undefined

    const predefinedFilter = quickFilterType
      ? {
          filterType: quickFilterType,
          dateRange: quickFilterDateField
            ? { start: quickFilterDateField[0], end: quickFilterDateField[1] }
            : undefined,
        }
      : undefined

    const sortBy = (() => {
      const value = casesFiltersStore.sortBy
      return value === 'AGE' ? CaseSorting.createdAt : value
    })()

    const sortOrder = (() => {
      const value = sortBy
        ? (String(casesFiltersStore.sortOrder)?.toUpperCase() as SortOrder)
        : undefined

      if (casesFiltersStore.sortBy !== 'AGE') return value

      if (value === SortOrder.ascending) return SortOrder.descending
      if (value === SortOrder.descending) return SortOrder.ascending

      return value
    })()

    return {
      ...otherFilters,
      pagination: { page: casesFiltersStore.page, size: casesFiltersStore.pageSize },
      sortBy,
      sortOrder,
      taskTypes: types,
      dueDatePeriod: formatPeriodDates(dueDate),
      createdAtPeriod: formatPeriodDates(createdAtPeriod),
      inStayGuestIssue: inStayGuestIssueFilter,
      isRecurringCaseStatus: recurringFilter,
      isAirbnbTripIssuesOnly: airbnbTripIssueFilter,
      hasExpense: hasExpenseFilter,
      aiGenerated: aiGeneratedFilter,
      predefinedFilter,
      recordTypes,
    }
  }

  private get enrichmentColumnKeys() {
    const columnKeys = this.gridColumns?.map(column => String(column.columnKey))
    return columnKeys && getEnrichmentColumnKeys(columnKeys)
  }

  private get casesPromise(): Promise<SearchCaseValue> {
    void [this.refresh]
    if (this.enrichmentColumnKeys == null) {
      return Promise.resolve({
        results: [],
        total: undefined,
        pages: undefined,
        hasNextPage: false,
      })
    }
    return arriere
      .searchCases({
        args: { ...this.caseFilters, skipTotal: true },
        columnKeys: this.enrichmentColumnKeys,
      })
      .then(response => {
        const results = response.values.results.map(aCase => ({
          ...aCase,
          home: { ...aCase.home, region: aCase.home.region ?? { id: '', name: '' } },
        }))
        return { ...response.values, results, hasNextPage: response.hasNextPage }
      })
  }

  private get getCaseInputsPromise() {
    return arriere.getCaseInputs()
  }

  searchCaseOwners = async (search: string) => {
    return arriere.searchCaseOwners(search)
  }

  searchCaseVendors = async (search: string, conciergeOnly?: boolean) => {
    return arriere.searchCaseVendors(search, conciergeOnly)
  }

  get caseInputs() {
    const caseInputs = getValue(this.getCaseInputsPromise) ?? []
    return caseInputs.reduce(
      (acc, curr) => {
        acc[curr.caseRecordType] = curr.inputs
        return acc
      },
      {} as Record<CaseRecordType, Partial<CaseInputs>>,
    )
  }

  get cases() {
    return (
      getValue(this.casesPromise) ?? {
        results: [],
        total: undefined,
        pages: undefined,
        hasNextPage: false,
      }
    )
  }

  get hasNextPage() {
    return this.cases.hasNextPage ?? false
  }

  get casesError() {
    return getError(this.casesPromise)
  }

  get isLoadingCases() {
    return this.enrichmentColumnKeys == null || isPending(this.casesPromise)
  }

  get isLoadingCaseInputs() {
    return isPending(this.getCaseInputsPromise)
  }

  get isAllRowsSelected() {
    return this.cases?.results?.every(data => this.selectedRowIndexes.has(data.workOrderId))
  }

  get isBullEditingCaseWithExpenseAndVendor() {
    const list = this.cases.results
    return list
      ?.filter(data => data.hasExpense && !!data.caseVendor)
      .some(data => this.selectedRowIndexes.has(data.workOrderId))
  }
  reloadTable() {
    this.refresh += 1
  }

  setGridColumns(columns: ColumnConfig<SearchCaseRowProps>[]) {
    this.gridColumns = columns
  }

  setEditInLine(value: boolean) {
    this.editionInline = value
  }

  updateCaseBasic = (data: CaseBasicDetailsUpdate) => {
    return arriere.updateCaseBasic(data).then(response => {
      if (response) {
        this.reloadTable()
      }
    })
  }

  setCaseDetailsId(value?: string): void {
    this.caseDetailsIdParam.push(value)
  }

  async exportCSV() {
    try {
      const filters = {
        ...this.caseFilters,
        pagination: { page: 1, size: DEFAULT_EXPORT_RECORDS_LIMIT },
        skipTotal: true,
      }

      const cases = await arriere.searchCases({ aliasPrefix: 'exportCSV', args: filters })
      if (cases.hasNextPage) {
        throw new Error('Exports are capped at 2,000 rows. Narrow your filters to export.')
      }
      const dataToExport = cases?.values?.results?.map(value => {
        const { expenseAgreement, name: homeName } = value.home

        return {
          ...value,
          recordType: value.caseTaxonomy ? value.caseTaxonomy.task : value.recordType,
          taskType: value.caseTaxonomy ? value.caseTaxonomy.serviceType : value.taskType,
          category: value.caseTaxonomy ? value.caseTaxonomy.category : value.category,
          subcategory: value.caseTaxonomy ? value.caseTaxonomy.subcategory : value.subcategory,
          age: getCaseAge(value.createdAt),
          assignedFo: value.home.assignedFieldOps?.name,
          home: homeName,
          caseOwner: value.caseOwner?.name,
          caseVendor: value.caseVendor?.name,
          recurringCaseSettingId: !!value.recurringCaseSettingId,
          dueDate: value.dueDate ? formatDate(value.dueDate) : '',
          dueTime: value.dueDate ? formatDate(value.dueDate, 'hh:mm a') : '',
          createdAt: value.createdAt ? formatDate(value.createdAt) : '',
          createTime: value.createdAt ? formatDate(value.createdAt, 'hh:mm a') : '',
          mtMarkupPercent: expenseAgreement ? expenseAgreement.mtMarkupPercent : undefined,
          spendApprovalThreshold: expenseAgreement
            ? expenseAgreement.spendApprovalThresholdInCents / 100
            : undefined,
          photoPercentage: value.stats?.photosMeanPercentage,
          checkListPercentage: value.stats?.workMeanPercentage,
          reviewer: value.lastReviewer?.name,
          market: value.home?.region?.name,
          bookingRating: getBookingRatingExportValues(value.caseBookingReviews),
        }
      }) as unknown as SearchCaseRowProps[]

      exportDataToCSV({
        data: dataToExport ?? [],
        columns: this.gridColumns ?? [],
        options: {
          filename: `cases-${formatDate(new Date(), 'MMddyyyyhhmm')}`,
          useTitles: true,
        },
        trackingInfo: {
          alias: 'exportCSV_cases',
          queryArgs: filters,
          totalRows: dataToExport?.length ?? 0,
        },
      })
    } catch (error) {
      handleStoreError({
        action: 'Failed to export cases',
        error,
        userMessage: error instanceof Error ? error.message : 'Could not export cases.',
      })
      throw error
    }
  }

  setSelectedRowIndexes(workOrderId: string, rowIndex: number, rowData: SearchCaseRowProps) {
    const isSelected = this.selectedRowIndexes.has(workOrderId)
    if (isSelected) {
      this.selectedRowIndexes.delete(workOrderId)
    } else {
      this.selectedRowIndexes.set(workOrderId, { rowIndex, rowData })
    }
  }

  overrideRowSelection(workOrderId: string, rowIndex: number) {
    this.selectedRowIndexes = new Map().set(workOrderId, rowIndex)
  }

  selectAllRowIndexes() {
    const list = this.cases.results
    if (this.isAllRowsSelected) {
      this.selectedRowIndexes.clear()
    } else {
      list.forEach((data, index) => {
        this.selectedRowIndexes.set(data.workOrderId, { rowIndex: index, rowData: data })
      })
    }
  }
}

export const casesStore = new _CasesStore()
